/**
 * 异步任务队列
 * 用于保证设备指令的有序执行，防止并发冲突
 */
export class CommandQueue {
  private queue: (() => Promise<void>)[] = []
  private running = false
  private paused = false

  constructor(
    private options: {
      /** 最小指令间隔 (ms) */
      interval?: number
      /** 错误处理 */
      onError?: (error: any) => void
    } = {},
  ) {}

  /**
   * 添加任务到队列
   * @param task 异步任务函数
   * @returns Promise，任务完成时 resolve
   */
  add<T>(task: () => Promise<T>, options: { notifyIoFailure?: boolean } = {}): Promise<T> {
    return new Promise((resolve, reject) => {
      const wrappedTask = async () => {
        try {
          const result = await task()
          resolve(result)
        } catch (error) {
          if (options.notifyIoFailure !== false && this.options.onError) {
            this.options.onError(error)
          }
          reject(error)
        }
      }

      this.queue.push(wrappedTask)
      this.process()
    })
  }

  /**
   * 暂停队列执行 (例如在 OTA 或宏录制时)
   */
  pause() {
    this.paused = true
  }

  /**
   * 恢复队列执行
   */
  resume() {
    this.paused = false
    this.process()
  }

  /**
   * 清空队列
   */
  clear() {
    this.queue = []
  }

  /**
   * 处理队列
   */
  private async process() {
    if (this.running || this.paused || this.queue.length === 0) {
      return
    }

    this.running = true

    while (this.queue.length > 0 && !this.paused) {
      const task = this.queue.shift()
      if (task) {
        try {
          await task()
          // 如果设置了间隔，等待一段时间
          if (this.options.interval && this.options.interval > 0) {
            await new Promise((resolve) => setTimeout(resolve, this.options.interval))
          }
        } catch (e) {
          // 任务内部已经处理了 resolve/reject，这里主要是防止队列崩溃
          console.error('[CommandQueue] Task execution failed', e)
        }
      }
    }

    this.running = false
  }
}
